import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { NotificationAlert } from "./NotificationAlert";
import type { TrayNotificationData } from "@/shared/utils/notificationTray";
import {
  ENABLE_NOTIFICATION_PREVIEW,
  getPreviewTray,
  NOTIFICATION_PREVIEW_KIND,
} from "@/shared/utils/notificationPreview";

interface NotificationContextType {
  showNotification: (title: string, body: string, data?: TrayNotificationData) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotificationAlert() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotificationAlert must be used within a NotificationAlertProvider");
  }
  return context;
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

export function NotificationAlertProvider({ children }: NotificationProviderProps) {
  const [notification, setNotification] = useState<{
    title: string;
    body: string;
    data?: TrayNotificationData;
    isVisible: boolean;
  }>({
    title: "",
    body: "",
    data: undefined,
    isVisible: false,
  });

  const [previewDismissed, setPreviewDismissed] = useState(false);

  useEffect(() => {
    if (!ENABLE_NOTIFICATION_PREVIEW) {
      setPreviewDismissed(false);
    }
  }, [ENABLE_NOTIFICATION_PREVIEW]);

  useLayoutEffect(() => {
    if (!ENABLE_NOTIFICATION_PREVIEW) return;
    setPreviewDismissed(false);
  }, [NOTIFICATION_PREVIEW_KIND]);

  const showNotification = useCallback((title: string, body: string, data?: TrayNotificationData) => {
    setNotification({
      title,
      body,
      data,
      isVisible: true,
    });
  }, []);

  const hideNotification = useCallback(() => {
    if (ENABLE_NOTIFICATION_PREVIEW) {
      setPreviewDismissed(true);
      return;
    }
    setNotification((prev) => ({
      ...prev,
      isVisible: false,
    }));
  }, []);

  useEffect(() => {
    const handleCustomNotification = (event: CustomEvent) => {
      const { title, body, data } = event.detail as {
        title?: string;
        body?: string;
        data?: TrayNotificationData;
      };
      const tray = data ?? {};
      const resolvedTitle = String(tray.title ?? title ?? "").trim();
      const resolvedBody = String(tray.body ?? body ?? "").trim();
      showNotification(resolvedTitle, resolvedBody, { ...tray, title: resolvedTitle, body: resolvedBody });
    };

    window.addEventListener("showNotification", handleCustomNotification as EventListener);

    return () => {
      window.removeEventListener("showNotification", handleCustomNotification as EventListener);
    };
  }, [showNotification]);

  const previewTray = ENABLE_NOTIFICATION_PREVIEW
    ? getPreviewTray(NOTIFICATION_PREVIEW_KIND)
    : null;

  const active =
    previewTray != null
      ? {
          title: previewTray.title,
          body: previewTray.body,
          data: previewTray.data,
          isVisible: !previewDismissed,
        }
      : notification;

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification }}>
      {children}
      <NotificationAlert
        title={active.title}
        body={active.body}
        isVisible={active.isVisible}
        onClose={hideNotification}
        data={active.data}
        isPersistent={ENABLE_NOTIFICATION_PREVIEW && !previewDismissed}
      />
    </NotificationContext.Provider>
  );
}
